'use client'

import { ChevronRight } from 'lucide-react'
import { useState } from 'react'

/**
 * Shared structured-data display components used by both NodeExecutionDetailsContent
 * and CopilotToolResultContent to render execution inputs/outputs.
 *
 * Keeping these in one place prevents the two call-sites from drifting apart.
 */

export function TypeBadge({ value }: { value: unknown }) {
  if (value === null) {
    return (
      <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
        null
      </span>
    )
  }
  if (value === undefined) {
    return (
      <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
        undefined
      </span>
    )
  }
  if (typeof value === 'string') {
    return (
      <span className="rounded border border-green-200 bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
        string
      </span>
    )
  }
  if (typeof value === 'number') {
    return (
      <span className="rounded border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
        number
      </span>
    )
  }
  if (typeof value === 'boolean') {
    return (
      <span className="rounded border border-purple-200 bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
        boolean
      </span>
    )
  }
  if (Array.isArray(value)) {
    return (
      <span className="rounded border border-red-200 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
        array
      </span>
    )
  }
  if (typeof value === 'object') {
    return (
      <span className="rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800">
        object
      </span>
    )
  }
  return (
    <span className="rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
      unknown
    </span>
  )
}

export function DataRenderer({ data, depth = 0 }: { data: unknown; depth?: number }) {
  const shouldAutoExpand = () => {
    if (Array.isArray(data)) return data.length <= 10
    if (typeof data === 'object' && data !== null) return Object.keys(data).length <= 5
    return false
  }

  const [isExpanded, setIsExpanded] = useState(shouldAutoExpand())
  const indentLevel = depth * 16

  if (data === null) return <span className="italic text-gray-600">null</span>
  if (data === undefined) return <span className="italic text-gray-600">undefined</span>

  if (typeof data === 'string') {
    return <span className="break-all font-mono text-sm text-gray-800">{JSON.stringify(data)}</span>
  }
  if (typeof data === 'number') {
    return <span className="font-mono text-sm text-gray-800">{data}</span>
  }
  if (typeof data === 'boolean') {
    return <span className="font-mono text-sm text-gray-800">{String(data)}</span>
  }

  if (Array.isArray(data)) {
    return (
      <div style={{ paddingLeft: `${indentLevel}px` }}>
        <div
          className={`${isExpanded ? 'mb-2' : ''} flex items-center gap-2 ${data.length > 0 ? '-mx-2 cursor-pointer rounded px-2 py-1 transition-colors hover:bg-gray-50' : ''}`}
          onClick={() => data.length > 0 && setIsExpanded(!isExpanded)}
        >
          <span className="text-xs text-gray-600">({data.length} items)</span>
          {data.length > 0 && (
            <ChevronRight
              size={14}
              className={`text-blue-600 transition-all duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
            />
          )}
        </div>
        {isExpanded && data.length > 0 && (
          <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
            {data.map((item, index) => (
              <div key={index}>
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">[{index}]</span>
                  <TypeBadge value={item} />
                </div>
                <DataRenderer data={item} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>)
    return (
      <div style={{ paddingLeft: `${indentLevel}px` }}>
        <div
          className={`${isExpanded ? 'mb-2' : ''} flex items-center gap-2 ${entries.length > 0 ? '-mx-2 cursor-pointer rounded px-2 py-1 transition-colors hover:bg-gray-50' : ''}`}
          onClick={() => entries.length > 0 && setIsExpanded(!isExpanded)}
        >
          <span className="text-xs text-gray-600">({entries.length} properties)</span>
          {entries.length > 0 && (
            <ChevronRight
              size={14}
              className={`text-blue-600 transition-all duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'}`}
            />
          )}
        </div>
        {isExpanded && entries.length > 0 && (
          <div className="ml-4 space-y-3 border-l-2 border-gray-200 pl-4">
            {entries.map(([key, value]) => (
              <div key={key}>
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">{key}</span>
                  <TypeBadge value={value} />
                </div>
                <DataRenderer data={value} depth={depth + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return <span className="font-mono text-sm text-gray-800">{String(data)}</span>
}
